/**
 * Buy More Save More – web component
 * Listens for cart changes (Rebuy + theme pub/sub) and updates visible state + remainder.
 */
class BuyMoreSaveMore extends HTMLElement {
  constructor() {
    super();
    this.thresholds = this.parseThresholds();
    this.percents = this.parsePercents();
    this.cartUpdateUnsubscriber = null;
  }

  parseThresholds() {
    try {
      const raw = this.dataset.thresholds;
      if (!raw) return [20000, 20000, 20000];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.length >= 3 ? arr.map((n) => parseInt(n, 10)) : [20000, 20000, 20000];
    } catch {
      return [20000, 20000, 20000];
    }
  }

  parsePercents() {
    try {
      const raw = this.dataset.percents;
      if (!raw) return [0, 0, 0];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.length >= 3 ? arr.map((n) => parseFloat(n)) : [0, 0, 0];
    } catch {
      return [0, 0, 0];
    }
  }

  getActiveState(totalPriceCents) {
    const [t2, t3, t4] = this.thresholds;
    if (totalPriceCents < t2) return 1;
    if (totalPriceCents < t3) return 2;
    if (totalPriceCents < t4) return 3;
    return 4;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(window.Shopify?.locale || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  }

  updateVisibility(activeState) {
    this.querySelectorAll('[data-state]').forEach((panel) => {
      const state = parseInt(panel.dataset.state, 10);
      panel.classList.toggle('hidden', state !== activeState);
    });
  }

  updateRemainder(activeState, totalPriceCents) {
    if (activeState === 4) return;
    const nextThreshold = this.thresholds[activeState - 1];
    const remainderCents = Math.max(0, nextThreshold - totalPriceCents);
    const panel = this.querySelector(`[data-state="${activeState}"]`);
    const remainderEl = panel?.querySelector('[data-remainder]');
    if (remainderEl) remainderEl.textContent = this.formatMoney(remainderCents);
  }

  updateAllTierRemainders(totalPriceCents) {
    this.thresholds.forEach((thresholdValue, index) => {
      const remainderCents = Math.max(0, thresholdValue - totalPriceCents);
      const els = this.querySelectorAll(`[data-remainder-${index + 1}]`);
      els.forEach((el) => {
        el.textContent = this.formatMoney(remainderCents);
      });
    });
  }

  updateSavings(activeState, totalPriceCents) {
    // No savings until at least first tier is active
    if (activeState <= 1) return;

    // Map states 2,3,4 -> percents index 0,1,2
    const percentIndex = activeState - 2;
    const percent = this.percents[percentIndex] || 0;
    if (!percent) return;

    const savingsCents = Math.round((totalPriceCents * percent) / 100);
    const panel = this.querySelector(`[data-state="${activeState}"]`);
    const savingsEl = panel?.querySelector('[data-savings]');
    if (savingsEl) savingsEl.textContent = this.formatMoney(savingsCents);
  }

  update(totalPriceCents) {
    const activeState = this.getActiveState(totalPriceCents);
    this.updateVisibility(activeState);
    this.updateRemainder(activeState, totalPriceCents);
    this.updateAllTierRemainders(totalPriceCents);
    this.updateSavings(activeState, totalPriceCents);
  }

  async updateFromCart(cartObject) {
    const cart = cartObject ?? (await fetch(`${routes.cart_url}.js`).then((r) => r.json()));
    if (!cart) return;

    // Prefer pre-discount subtotal when available
    const subtotalCents = cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price;

    if (subtotalCents != null) this.update(subtotalCents);
  }

  connectedCallback() {
    document.addEventListener(
      'rebuy:cart.change',
      (this.handleRebuyCartChange = (event) => {
        const cart = event.detail?.cart?.cart;
        if (!cart) return;

        const subtotalCents = cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price;

        if (subtotalCents != null) this.update(subtotalCents);
      }),
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (response) => {
        const cartFromEvent = response?.cartData;
        if (cartFromEvent) {
          this.updateFromCart(cartFromEvent);
          return;
        }

        this.updateFromCart();
      });
    }

    this.updateFromCart();
  }

  disconnectedCallback() {
    document.removeEventListener('rebuy:cart.change', this.handleRebuyCartChange);
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }
}

if (customElements.get('buy-more-save-more') === undefined) {
  customElements.define('buy-more-save-more', BuyMoreSaveMore);
}
